import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import { 
  Warehouse as WarehouseIcon, Plus, Filter, Search, Package, 
  TrendingUp, DollarSign, Clock, CheckCircle, XCircle, 
  AlertTriangle, ArrowUpRight, ArrowDownRight, Download,
  RefreshCw, Eye, Edit, MoreVertical, Calendar, MapPin,
  Truck, Globe, Shield, FileText, BarChart3, PieChart,
  Activity, ShoppingCart, CreditCard, Receipt, Thermometer,
  Droplets, Zap, Box, Palette, Layers, Archive
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, ScatterChart, Scatter
} from 'recharts';

const Warehouse = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [timeRange, setTimeRange] = useState('7d');
  const { isDarkMode } = useChainsTheme();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock data for warehouse management
  const warehouseData = [
    { 
      id: 'WH-001', 
      name: 'Riyadh Central', 
      location: 'Riyadh', 
      capacity: 85000, 
      utilized: 66500, 
      utilization: 78, 
      temperature: 22, 
      humidity: 45, 
      alerts: 2,
      status: 'active',
      lastUpdated: '2024-01-15 14:30',
      zones: 12,
      staff: 24,
      efficiency: 94.2
    },
    { 
      id: 'WH-002', 
      name: 'Jeddah Port', 
      location: 'Jeddah', 
      capacity: 92000, 
      utilized: 78200, 
      utilization: 85, 
      temperature: 24, 
      humidity: 52, 
      alerts: 1,
      status: 'active',
      lastUpdated: '2024-01-15 14:25',
      zones: 15,
      staff: 32,
      efficiency: 96.8
    },
    { 
      id: 'WH-003', 
      name: 'Dammam Industrial', 
      location: 'Dammam', 
      capacity: 78000, 
      utilized: 56160, 
      utilization: 72, 
      temperature: 21, 
      humidity: 48, 
      alerts: 0,
      status: 'active',
      lastUpdated: '2024-01-15 14:20',
      zones: 10,
      staff: 18,
      efficiency: 91.5
    },
    { 
      id: 'WH-004', 
      name: 'Abha Regional', 
      location: 'Abha', 
      capacity: 65000, 
      utilized: 37700, 
      utilization: 58, 
      temperature: 19, 
      humidity: 42, 
      alerts: 3,
      status: 'maintenance',
      lastUpdated: '2024-01-15 14:15',
      zones: 8,
      staff: 12,
      efficiency: 88.7
    },
    { 
      id: 'WH-005', 
      name: 'Tabuk Storage', 
      location: 'Tabuk', 
      capacity: 88000, 
      utilized: 71280, 
      utilization: 81, 
      temperature: 23, 
      humidity: 50, 
      alerts: 1,
      status: 'active',
      lastUpdated: '2024-01-15 14:10',
      zones: 14,
      staff: 28,
      efficiency: 93.4
    }
  ];

  const inventoryData = [
    { 
      id: 'INV-001', 
      name: 'Laptop Computers', 
      category: 'Electronics', 
      quantity: 1250, 
      value: 1875000, 
      location: 'WH-001', 
      zone: 'A-3', 
      status: 'in_stock',
      minStock: 100,
      maxStock: 2000,
      supplier: 'TechCorp Solutions',
      lastRestocked: '2024-01-10',
      expiryDate: null
    },
    { 
      id: 'INV-002', 
      name: 'Office Chairs', 
      category: 'Furniture', 
      quantity: 850, 
      value: 425000, 
      location: 'WH-002', 
      zone: 'B-1', 
      status: 'low_stock',
      minStock: 200,
      maxStock: 1000,
      supplier: 'Premium Suppliers',
      lastRestocked: '2024-01-08',
      expiryDate: null
    },
    { 
      id: 'INV-003', 
      name: 'Printer Cartridges', 
      category: 'Office Supplies', 
      quantity: 3200, 
      value: 256000, 
      location: 'WH-001', 
      zone: 'C-2', 
      status: 'in_stock',
      minStock: 500,
      maxStock: 5000,
      supplier: 'Office Supplies Co.',
      lastRestocked: '2024-01-12',
      expiryDate: '2025-06-15'
    },
    { 
      id: 'INV-004', 
      name: 'Network Switches', 
      category: 'IT Equipment', 
      quantity: 180, 
      value: 720000, 
      location: 'WH-003', 
      zone: 'A-1', 
      status: 'critical',
      minStock: 50,
      maxStock: 300,
      supplier: 'TechCorp Solutions',
      lastRestocked: '2024-01-05',
      expiryDate: null
    }
  ];

  const performanceMetrics = [
    { name: 'Total Capacity', value: '408K sq ft', change: '+5%', trend: 'up', icon: Box, color: 'blue' },
    { name: 'Utilization Rate', value: '74.8%', change: '+2.1%', trend: 'up', icon: TrendingUp, color: 'green' },
    { name: 'Active Warehouses', value: '5', change: '+1', trend: 'up', icon: WarehouseIcon, color: 'purple' },
    { name: 'Total Inventory', value: 'SAR 3.2M', change: '+8.5%', trend: 'up', icon: Package, color: 'emerald' },
    { name: 'Efficiency Score', value: '92.9%', change: '+1.2%', trend: 'up', icon: CheckCircle, color: 'green' },
    { name: 'Active Alerts', value: '7', change: '-2', trend: 'down', icon: AlertTriangle, color: 'red' }
  ];

  const categoryData = [
    { name: 'Electronics', value: 35, color: '#3B82F6' },
    { name: 'Furniture', value: 25, color: '#10B981' },
    { name: 'Office Supplies', value: 20, color: '#F59E0B' },
    { name: 'IT Equipment', value: 15, color: '#EF4444' },
    { name: 'Raw Materials', value: 5, color: '#8B5CF6' }
  ];

  const temperatureData = [
    { time: '00:00', temp: 21, humidity: 45 },
    { time: '04:00', temp: 20, humidity: 48 },
    { time: '08:00', temp: 22, humidity: 42 },
    { time: '12:00', temp: 24, humidity: 38 },
    { time: '16:00', temp: 25, humidity: 35 },
    { time: '20:00', temp: 23, humidity: 40 }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'maintenance': return 'text-orange-400';
      case 'inactive': return 'text-red-400';
      case 'in_stock': return 'text-green-400';
      case 'low_stock': return 'text-yellow-400';
      case 'critical': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20';
      case 'maintenance': return 'bg-orange-500/20';
      case 'inactive': return 'bg-red-500/20';
      case 'in_stock': return 'bg-green-500/20';
      case 'low_stock': return 'bg-yellow-500/20';
      case 'critical': return 'bg-red-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 90) return 'bg-red-500';
    if (utilization >= 80) return 'bg-orange-500';
    if (utilization >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const WarehouseCard = ({ warehouse }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{warehouse.name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(warehouse.status)} ${getStatusColor(warehouse.status)}`}>
              {warehouse.status}
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{warehouse.location} • {warehouse.zones} zones</p>
        </div>
        <div className={`w-4 h-4 rounded-full ${getUtilizationColor(warehouse.utilization)}`}></div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Capacity</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{warehouse.capacity.toLocaleString()} sq ft</p>
        </div>
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Utilization</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{warehouse.utilization}%</p>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Temperature</span>
          <span className={`font-medium ${warehouse.temperature > 25 ? 'text-red-400' : 'text-green-400'}`}>
            {warehouse.temperature}°C
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Humidity</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{warehouse.humidity}%</span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Efficiency</span>
          <span className={`font-medium ${warehouse.efficiency >= 90 ? 'text-green-400' : 'text-orange-400'}`}>
            {warehouse.efficiency}%
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Alerts</span>
          <span className={`font-medium ${warehouse.alerts > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {warehouse.alerts}
          </span>
        </div>
      </div>

      {/* Heatmap Visualization */}
      <div className="mt-4">
        <div className="flex justify-between text-xs mb-2">
          <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>Storage Zones</span>
          <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>{warehouse.utilization}% Full</span>
        </div>
        <div className="grid grid-cols-8 gap-1">
          {[...Array(64)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.01 }}
              className={`h-3 rounded-sm ${
                i < (warehouse.utilization / 100) * 64
                  ? getUtilizationColor(warehouse.utilization)
                  : isDarkMode ? 'bg-white/10' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="text-sm">
          <p className={`${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Staff: {warehouse.staff}</p>
          <p className={`${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Updated: {warehouse.lastUpdated}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Eye className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Edit className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );

  const InventoryCard = ({ item }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(item.status)} ${getStatusColor(item.status)}`}>
              {item.status.replace('_', ' ')}
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{item.category} • {item.location}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Quantity</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.quantity.toLocaleString()}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Zone</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.zone}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Min Stock</p>
          <p className={`font-medium ${item.quantity <= item.minStock ? 'text-red-400' : 'text-green-400'}`}>
            {item.minStock}
          </p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Supplier</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.supplier}</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="text-right">
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {item.value.toLocaleString()}</p>
          <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Total Value</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Eye className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Edit className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 space-y-8 h-full overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
                  <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Warehouse & Inventory Management</h1>
        <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-gray-700'}`}>Storage optimization, inventory tracking, and warehouse analytics</p>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <Download className="w-5 h-5 mr-2 inline" />
            Export
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            Add Inventory
          </motion.button>
        </div>
      </motion.div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {performanceMetrics.map((metric, index) => (
          <motion.div
            key={metric.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`backdrop-blur-sm rounded-2xl p-6 ${
              isDarkMode 
                ? 'bg-white/10 border border-white/20' 
                : 'bg-white/70 border border-gray-200/50'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-${metric.color}-500/20`}>
                <metric.icon className={`w-6 h-6 text-${metric.color}-500`} />
              </div>
              <div className={`flex items-center text-sm ${
                metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
              }`}>
                {metric.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 mr-1" />
                )}
                {metric.change}
              </div>
            </div>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.value}</p>
            <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{metric.name}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Temperature & Humidity Monitoring */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Environmental Monitoring</h3>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Temperature</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Humidity</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={temperatureData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#ffffff20' : '#f0f0f0'} />
              <XAxis dataKey="time" stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <YAxis stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
              <Line type="monotone" dataKey="temp" stroke="#EF4444" strokeWidth={3} />
              <Line type="monotone" dataKey="humidity" stroke="#3B82F6" strokeWidth={3} />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Inventory Distribution */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Inventory by Category</h3>
            <button className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline`}>View All</button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {categoryData.map((category, index) => (
              <div key={category.name} className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>{category.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-1 p-1 backdrop-blur-sm rounded-xl border border-white/20">
        {[
          { id: 'overview', label: 'Overview', count: warehouseData.length + inventoryData.length },
          { id: 'warehouses', label: 'Warehouses', count: warehouseData.length },
          { id: 'inventory', label: 'Inventory', count: inventoryData.length },
          { id: 'analytics', label: 'Analytics', count: 8 }
        ].map((tab) => (
          <motion.button
            key={tab.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg'
                : isDarkMode 
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <span>{tab.label}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
              activeTab === tab.id ? 'bg-white/20' : isDarkMode ? 'bg-white/10' : 'bg-gray-100'
            }`}>
              {tab.count}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Content based on active tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'warehouses' && (
          <motion.div
            key="warehouses"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {warehouseData.map((warehouse, index) => (
              <motion.div
                key={warehouse.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <WarehouseCard warehouse={warehouse} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'inventory' && (
          <motion.div
            key="inventory"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {inventoryData.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <InventoryCard item={item} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Warehouse Status</h3>
              {warehouseData.slice(0, 2).map((warehouse, index) => (
                <motion.div
                  key={warehouse.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <WarehouseCard warehouse={warehouse} />
                </motion.div>
              ))}
            </div>
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Critical Inventory</h3>
              {inventoryData.filter(item => item.status === 'critical' || item.status === 'low_stock').slice(0, 2).map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <InventoryCard item={item} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Warehouse; 